import { useUserStore } from '@/store/userStore';
import { trpc } from '@/trpcClient';
import type { DiscoveredFlowSensorDto } from '@shelflix/trpc/routes/flowSensor/discovered/useCase';
import type { FlowSensorDto } from '@shelflix/trpc/routes/flowSensor/list/useCase';
import { ConfirmationModal } from '@shelflix/ui-web/components/ConfirmationModal';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import Icon from '@shelflix/ui-web/components/Icon';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { SensorFormDialog } from './SensorFormDialog';
import { formatDateTime, formatNumber } from './format';
import { useFlowSensorAccess } from './useFlowSensorAccess';

function OnlineBadge({ online }: { online: boolean }) {
  return (
    <Badge
      className={`h-6 rounded-sm px-2 text-xs font-semibold ${
        online ? 'bg-[var(--active)] text-[var(--active-dark)]' : 'bg-[var(--inactive)] text-[var(--inactive-dark)]'
      }`}
    >
      <Icon name={online ? 'wifi' : 'wifi_off'} size={14} className="mr-1" />
      {online ? 'Online' : 'Offline'}
    </Badge>
  );
}

export function SensorsTab({
  sensors,
  isLoading,
  refetch,
}: {
  sensors: FlowSensorDto[];
  isLoading: boolean;
  refetch: () => void;
}) {
  const { canManage } = useFlowSensorAccess();
  const isMaster = useUserStore((s) => s.user?.isMaster ?? false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FlowSensorDto | null>(null);
  const [fromDiscovered, setFromDiscovered] = useState<DiscoveredFlowSensorDto | null>(null);
  const [removing, setRemoving] = useState<FlowSensorDto | null>(null);

  const discovered = useQuery({
    ...trpc.flowSensor.discovered.queryOptions(),
    enabled: canManage && isMaster,
    refetchInterval: 60_000,
  });
  const remove = useMutation(trpc.flowSensor.remove.mutationOptions());

  const onSaved = () => {
    refetch();
    discovered.refetch();
  };

  const openNew = (d: DiscoveredFlowSensorDto | null) => {
    setEditing(null);
    setFromDiscovered(d);
    setFormOpen(true);
  };

  const columns: Array<{
    header: string;
    key: keyof FlowSensorDto;
    render?: (value: FlowSensorDto[keyof FlowSensorDto], row: FlowSensorDto) => React.ReactNode;
  }> = [
    {
      header: 'Sensor',
      key: 'name',
      render: (_, row) => (
        <div className="flex min-w-[180px] flex-col">
          <span className="font-medium text-[var(--gray-primary)]">{row.name}</span>
          <span className="text-xs text-[var(--gray-secondary)]">
            {row.product ?? row.sensorPosition ?? '—'} · {row.mac}
          </span>
        </div>
      ),
    },
    { header: 'Estabelecimento', key: 'facility', render: (_, row) => row.facility?.name ?? '—' },
    { header: 'Status', key: 'online', render: (_, row) => <OnlineBadge online={row.online} /> },
    { header: 'Hoje', key: 'todayCount', render: (_, row) => formatNumber(row.todayCount) },
    { header: 'Última aproximação', key: 'lastEventAt', render: (_, row) => formatDateTime(row.lastEventAt) },
    {
      header: 'Fila no sensor',
      key: 'queuePending',
      render: (_, row) => (row.queuePending ? `${formatNumber(row.queuePending)} aguardando` : '—'),
    },
    {
      header: 'Ações',
      key: 'uuid',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            disabled={!canManage}
            onClick={() => {
              setEditing(row);
              setFromDiscovered(null);
              setFormOpen(true);
            }}
          >
            <Icon name="edit" className="text-primary" />
          </Button>
          <Button variant="ghost" className="h-8 w-8 p-0" disabled={!canManage} onClick={() => setRemoving(row)}>
            <Icon name="delete" className={canManage ? 'text-primary' : ''} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--gray-secondary)]">
          Receptores que contam as aproximações. O status chega a cada minuto; cada aproximação chega com a data e a
          hora em que aconteceu, mesmo se a internet da loja cair.
        </p>
        {canManage && (
          <Button onClick={() => openNew(null)}>
            <Icon name="add" className="mr-1" /> Cadastrar sensor
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="p-4">Carregando sensores...</p>
      ) : sensors.length === 0 ? (
        <p className="p-4 text-[var(--gray-secondary)]">Nenhum sensor cadastrado.</p>
      ) : (
        <GenericTable
          columns={columns}
          data={sensors}
          itemsPerPages={50}
          totalItems={sensors.length}
          page={1}
          onPageChange={() => {}}
        />
      )}

      {canManage && isMaster && (
        <Card>
          <CardHeader>
            <CardTitle>Sensores encontrados no broker</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm text-[var(--gray-secondary)]">
              Receptores que já estão publicando e ainda não têm cadastro (visível só para usuários master).
            </p>
            {!discovered.data?.length ? (
              <p className="text-sm">Nenhum sensor novo.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-[var(--gray-secondary)]">
                  <tr>
                    <th className="py-2 font-medium">MAC</th>
                    <th className="py-2 font-medium">Nome no sensor</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium">Visto por último</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {discovered.data.map((d) => (
                    <tr key={d.mac} className="border-t">
                      <td className="py-2 font-mono">{d.mac}</td>
                      <td className="py-2">{d.sensorPosition ?? '—'}</td>
                      <td className="py-2">
                        <OnlineBadge online={d.online} />
                      </td>
                      <td className="py-2">{formatDateTime(d.lastSeenAt)}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => openNew(d)}>
                          Cadastrar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      <SensorFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        sensor={editing}
        initialMac={fromDiscovered?.mac}
        initialProduct={fromDiscovered?.sensorPosition}
        onSaved={onSaved}
      />
      <ConfirmationModal
        title="Remover sensor"
        description={`Remover o cadastro de "${removing?.name ?? ''}"? O sensor continua publicando e volta para a lista de encontrados; um novo cadastro não verá os dados antigos.`}
        open={!!removing}
        onCancel={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(
            { uuid: removing.uuid },
            {
              onSuccess: () => {
                toast.success('Cadastro removido');
                setRemoving(null);
                onSaved();
              },
              onError: (error) => toast.error(error.message),
            }
          )
        }
        confirmButtonVariant="destructive"
        loading={remove.isPending}
      />
    </div>
  );
}
